import {
  AdviserBatchPatchForm,
  AdviserPatchForm,
  AgentProfile,
  Invitee,
  InviteManyAdvisersForm
} from "@closerplatform/spinner-openapi";
import { caseInsensitiveIncludes } from "@fidget/common/utils/searchMatching";
import { validateEmail, validateRequired } from "@fidget/common/utils/validatorsFormState";
import { FieldState, FormState } from "formstate";
import {
  ExistingAdviserForm,
  ExistingAdviserFormFields,
  InviteAdvisersForm,
  NewAdviser
} from "../types";

export const mapAdviserToForm = (adviser: AgentProfile): ExistingAdviserForm => {
  return new FormState<ExistingAdviserFormFields>({
    id: new FieldState(adviser.id),
    email: new FieldState(adviser.email || ""),
    firstName: new FieldState(adviser.firstName),
    lastName: new FieldState(adviser.lastName),
    avatarToken: new FieldState(adviser.avatarToken || ""),
    tagIds: new FieldState(adviser.tags),
    tagGroupIds: new FieldState(adviser.tagGroups),
    roleId: new FieldState(adviser.orgRole),
    isActive: new FieldState(adviser.isActive),
    presence: new FieldState(adviser.presence),
    agentGroupIds: new FieldState(adviser.groups),
    validFrom: new FieldState(adviser.validFrom),
    validTo: new FieldState(adviser.validTo),
    autoAssignedLimit: new FieldState(adviser.autoAssignedLimit)
  });
};

export const createInviteFormAdviser = (): NewAdviser => {
  return new FormState({
    email: new FieldState("").validators(validateEmail, validateRequired),
    firstName: new FieldState(""),
    lastName: new FieldState("")
  });
};

export const createInviteForm = (): InviteAdvisersForm => {
  return new FormState<NewAdviser[]>([createInviteFormAdviser()]);
};

export const copyExistingAdviserForm = (form: ExistingAdviserForm): ExistingAdviserForm => {
  return new FormState<ExistingAdviserFormFields>({
    id: form.$.id,
    email: form.$.email,
    firstName: form.$.firstName,
    lastName: form.$.lastName,
    avatarToken: form.$.avatarToken,
    tagIds: new FieldState(form.$.tagIds.$),
    tagGroupIds: new FieldState(form.$.tagGroupIds.$),
    agentGroupIds: new FieldState(form.$.agentGroupIds.$),
    roleId: new FieldState(form.$.roleId.$),
    isActive: new FieldState(form.$.isActive.$),
    validFrom: new FieldState(form.$.validFrom.$),
    validTo: new FieldState(form.$.validTo.$),
    autoAssignedLimit: new FieldState(form.$.autoAssignedLimit.$),
    presence: form.$.presence
  });
};

export const mapInviteFormToApiForm = (form: InviteAdvisersForm): InviteManyAdvisersForm => {
  const invitations = form.$.map((adviserForm) => ({
    email: adviserForm.$.email.$,
    lastName: adviserForm.$.lastName.$,
    firstName: adviserForm.$.firstName.$
  }));

  return { invitations };
};

export const mapInviteFormToInvitees = (form: InviteAdvisersForm): Invitee[] => {
  return form.$.map((adviser) => {
    return {
      email: adviser.$.email.$,
      lastName: adviser.$.lastName.$,
      firstName: adviser.$.firstName.$
    };
  });
};

export const mapExistingFormToPatchForms = (form: ExistingAdviserForm): AdviserPatchForm => {
  return {
    roleId: form.$.roleId.$,
    validFrom: getNumberAsNullablePatchValue(form.$.validFrom.$),
    validTo: getNumberAsNullablePatchValue(form.$.validTo.$),
    tagGroupIds: form.$.tagGroupIds.$,
    autoAssignedLimit: getNumberAsNullablePatchValue(form.$.autoAssignedLimit.$),
    tagIds: form.$.tagIds.$
  };
};

export const mapExistingFormToBatchPatchForms = (form: ExistingAdviserForm): AdviserBatchPatchForm => {
  return {
    id: form.$.id.$,
    roleId: form.$.roleId.$,
    validFrom: getNumberAsNullablePatchValue(form.$.validFrom.$),
    validTo: getNumberAsNullablePatchValue(form.$.validTo.$),
    tagGroupIds: form.$.tagGroupIds.$,
    autoAssignedLimit: getNumberAsNullablePatchValue(form.$.autoAssignedLimit.$),
    tagIds: form.$.tagIds.$
  };
};

export const filterInputPredicate = (item: ExistingAdviserForm, input: string): boolean =>
  filterAgentPredicate(input, item.$.firstName.$, item.$.lastName.$, item.$.email.$);

export const filterAgentPredicate = (
  input: string,
  firstName: string,
  lastName: string,
  email: string
): boolean => {
  const trimmedInput = input.trim();

  return (
    caseInsensitiveIncludes(`${firstName.trim()} ${lastName.trim()}`, trimmedInput) ||
    caseInsensitiveIncludes(email.trim(), trimmedInput)
  );
};

// null as number - limitation of openapi generator that cannot generate nullable
const getNumberAsNullablePatchValue = (value: number | undefined): number =>
  // tslint:disable-next-line:no-null-keyword
  value === undefined ? (null as unknown as number) : value;
